'use client';

import React, { useEffect, useRef, useState } from 'react';
import { SideNav, SideNavMenuItem } from '@carbon/react';

interface TableOfContentProps {
  navigationItems?: { label: string; href?: string }[];
  hideTitle?: boolean;
  defaultActiveFirst?: boolean;
}

/* -------------------------------------------
   Helper: Generate unique IDs
-------------------------------------------- */
function generateUniqueId(base: string, usedIds: Set<string>) {
  let id = base;
  let counter = 1;

  while (usedIds.has(id)) {
    id = `${base}-${counter}`;
    counter++;
  }

  usedIds.add(id);
  return id;
}

export default function TableOfContent({
  navigationItems = [],
  hideTitle = false,
  defaultActiveFirst = false,
}: TableOfContentProps) {
  const [headings, setHeadings] = useState<{ id: string; text: string; level: number }[]>([]);
  const [activeId, setActiveId] = useState('');
  const [showOverview, setShowOverview] = useState(true);

  const observerRef = useRef<IntersectionObserver | null>(null);
  const clickLock = useRef(false);
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);

  /* -------------------------------------------
     Extract headings from DOM or navigationItems
     ✅ Only update state if IDs change
  -------------------------------------------- */
  useEffect(() => {
    const usedIds = new Set<string>();
    const newHeadings: { id: string; text: string; level: number }[] = [];

    if (navigationItems.length > 0) {
      navigationItems.forEach(item => {
        const baseId = item.href?.replace('#', '') || 'section';
        const id = generateUniqueId(baseId, usedIds);

        newHeadings.push({ id, text: item.label, level: 2 });
      });
    } else {
      const elements = Array.from(document.querySelectorAll<HTMLElement>('h2, h3, h4'));

      elements.forEach(el => {
        const text = el.textContent?.trim();
        if (!text) return;

        const baseId =
          el.id ||
          text
            .toLowerCase()
            .replace(/[^\w\s-]/g, '')
            .replace(/\s+/g, '-');

        const id = generateUniqueId(baseId, usedIds);

        if (el.id !== id) el.id = id;

        newHeadings.push({ id, text, level: Number(el.tagName.substring(1)) });
      });
    }

    // ✅ Only update state if IDs actually changed
    setHeadings(prev => {
      const prevIds = prev.map(h => h.id).join(',');
      const nextIds = newHeadings.map(h => h.id).join(',');
      return prevIds === nextIds ? prev : newHeadings;
    });
  }, [navigationItems]);

  /* -------------------------------------------
     Set first item as active by default when using navigationItems
   -------------------------------------------- */
  useEffect(() => {
    if (defaultActiveFirst && navigationItems.length > 0 && headings.length > 0 && !activeId) {
      setActiveId(headings[0].id);
    }
  }, [headings, navigationItems.length, activeId, defaultActiveFirst]);

  /* -------------------------------------------
     IntersectionObserver to track active heading
   -------------------------------------------- */
  // console.log('navigationItems-->', navigationItems);
  useEffect(() => {
    if (!headings.length) return;

    const sections = headings
      .map(h => document.getElementById(h.id))
      .filter(Boolean) as HTMLElement[];

    observerRef.current?.disconnect();

    observerRef.current = new IntersectionObserver(
      entries => {
        if (clickLock.current) return;

        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      },
      {
        rootMargin: '-40% 0px -50% 0px',
        threshold: 0.1,
      },
    );

    sections.forEach(section => observerRef.current?.observe(section));

    return () => observerRef.current?.disconnect();
  }, [headings]);

  /* -------------------------------------------
     Activate Overview if above first heading
  -------------------------------------------- */
  useEffect(() => {
    if (!headings.length) return;

    const first = document.getElementById(headings[0].id);
    if (!first) return;

    const onScroll = () => {
      if (clickLock.current) return;
      if (window.scrollY < first.offsetTop - 80) {
        setActiveId('');
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [headings]);

  /* -------------------------------------------
     Show Overview only if content exists before first heading
  -------------------------------------------- */
  useEffect(() => {
    if (!headings.length) {
      setShowOverview(false);
      return;
    }

    const firstHeading = document.getElementById(headings[0].id);
    if (!firstHeading) {
      setShowOverview(false);
      return;
    }

    let node = firstHeading.previousElementSibling as HTMLElement | null;
    let hasParagraphBefore = false;

    while (node) {
      if (node.tagName === 'P') {
        hasParagraphBefore = true;
        break;
      }
      if (/^H[2-4]$/.test(node.tagName)) break;
      node = node.previousElementSibling as HTMLElement | null;
    }

    setShowOverview(hasParagraphBefore);
  }, [headings]);

  /* -------------------------------------------
     Click handlers
  -------------------------------------------- */
  const handleClick = (id: string) => {
    clickLock.current = true;
    setActiveId(id);

    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    if (scrollTimeout.current) clearTimeout(scrollTimeout.current);

    scrollTimeout.current = setTimeout(() => {
      clickLock.current = false;
    }, 900);
  };

  const scrollToTop = () => {
    clickLock.current = true;
    setActiveId('');

    window.scrollTo({ top: 0, behavior: 'smooth' });

    setTimeout(() => {
      clickLock.current = false;
    }, 900);
  };

  /* -------------------------------------------
     Render
  -------------------------------------------- */
  return (
    <div className="tag-side-nav-container">
      {!hideTitle && <p className="side-nav-title">On this page</p>}

      <SideNav className="leftbar" isFixedNav expanded aria-label="Side navigation">
        {showOverview && (
          <SideNavMenuItem
            href="#overview"
            className={!activeId ? 'active' : ''}
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
              const target = e.currentTarget as HTMLElement;
              target.blur();
              // Remove focus immediately
              setTimeout(() => target.blur(), 0);
              scrollToTop();
            }}
            onMouseDown={(e: React.MouseEvent) => {
              e.preventDefault();
            }}
            onFocus={(e: React.FocusEvent) => {
              (e.currentTarget as HTMLElement).blur();
            }}
          >
            Overview
          </SideNavMenuItem>
        )}

        {headings.map(h => (
          <SideNavMenuItem
            key={h.id}
            href={`#${h.id}`}
            className={activeId === h.id ? 'active' : ''}
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
              const target = e.currentTarget as HTMLElement;
              target.blur();
              // Remove focus immediately
              setTimeout(() => target.blur(), 0);
              handleClick(h.id);
            }}
            onMouseDown={(e: React.MouseEvent) => {
              e.preventDefault();
            }}
            onFocus={(e: React.FocusEvent) => {
              (e.currentTarget as HTMLElement).blur();
            }}
          >
            {h.text}
          </SideNavMenuItem>
        ))}
      </SideNav>
    </div>
  );
}
